import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Chart } from "../charts/engine";
import {
  chartOption,
  COLOURS,
  drawable as firstTwoUnits,
  MIN_ZOOM_MS,
  zoomExtent,
  type ChartLine,
} from "../charts/option";
import { durationMs, RANGES, summarise, toChartSeries } from "../charts/series";
import { useWheelCapture } from "../charts/wheel";
import { expectedOf, type Band } from "../expected";
import { boundPoint, Frame, key, Note, useWidgetText } from "../shared";
import type { ChartRange, WidgetProps } from "../types";

/**
 * line_chart and bar_chart (WEB-F-DEV-04, DDB-36, DDB-37): the history of every bound datapoint (up to eight, from
 * any devices), stacked when the widget says so, a second unit on a right-hand axis, with a range picker where the
 * page offers one.
 */
export function HistoryChart({
  widget,
  input,
  kind,
  range,
  onRange,
  maxRangeMs,
  rangeNote,
}: WidgetProps & { kind: "line" | "bar" }) {
  const fmt = useWidgetText();
  const { t } = useTranslation();
  const count = (widget.bindings ?? []).filter((b) => b.datapoint).length;
  const bound = Array.from({ length: count }, (_, i) => boundPoint(widget, input, i)).filter((b) => !!b);
  // Lines from several devices are named "<device> · <reading>" (DDB-36).
  const several = new Set(bound.map((bp) => bp.binding.device)).size > 1;
  const parts = bound.map((bp) => {
    const dp = bp.binding.datapoint ?? "";
    const reading = bp.point?.name ?? (count === 1 ? widget.title : undefined) ?? dp;
    return {
      name: several && bp.device?.name ? `${bp.device.name} · ${reading}` : reading,
      unit: bp.point?.unit ?? null,
      decimals: bp.point?.decimals ?? 0,
      expected: expectedOf(bp.point),
      history: input.history?.[key(bp.binding.device, dp)],
    };
  });
  const shownParts = firstTwoUnits(parts);
  const dropped = parts.length - shownParts.length;
  const lines = useLines(shownParts);
  const stats = lines.map((l) => summarise(l.series));
  const any = stats.some((s) => !!s);
  const units = [...new Set(lines.map((l) => l.unit).filter((u): u is string => !!u))];
  const stacked = widget.config?.stacked === true;
  const configured = typeof widget.config?.range === "string" ? widget.config.range : "24h";
  const ranges = RANGES.includes(configured as (typeof RANGES)[number]) ? [...RANGES] : [configured, ...RANGES];
  const shown = range ?? configured;
  // A preset longer than the plan shows is disabled (plan-limits PL-41); the server would answer only the window.
  const tooLong = (r: string) => maxRangeMs !== undefined && (durationMs(r) ?? 0) > maxRangeMs;
  const limited = onRange && ranges.some(tooLong);

  const host = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const zoom = !!onRange;
  // A zoomed range: the chart can zoom back out and pan from it, past the data it has (zoomExtent).
  const from = typeof shown === "object" ? Date.parse(shown.from) : NaN;
  const to = typeof shown === "object" ? Date.parse(shown.to) : NaN;
  const zoomed = useMemo(() => (zoom && from < to ? ([from, to] as [number, number]) : undefined), [zoom, from, to]);
  const option = useMemo(
    () =>
      lines.some((l) => summarise(l.series))
        ? chartOption(lines, { kind, locale: fmt.locale, zoom, stacked, window: zoomed })
        : undefined,
    [lines, kind, fmt.locale, zoom, stacked, zoomed],
  );
  // What the zoom's percentages are of: the axis around a zoomed range, else the data.
  const times = useMemo(() => {
    if (zoomed) return zoomExtent(zoomed);
    const all = lines.flatMap((l) => l.series.times);
    return all.length ? [Math.min(...all), Math.max(...all)] : [];
  }, [lines, zoomed]);
  const optionRef = useRef(option);
  const onRangeRef = useRef(onRange);
  const timesRef = useRef(times);
  useEffect(() => {
    optionRef.current = option;
    onRangeRef.current = onRange;
    timesRef.current = times;
  });
  const chartRef = useRef<Chart | undefined>(undefined);

  // Drawn once the host has a size; ECharts is imported only then (WEB-F-Q-02). Without ResizeObserver (jsdom) the
  // chart is not drawn, and the text alternative carries the data.
  const drawable = any;
  useEffect(() => {
    const el = host.current;
    if (!el || !drawable || typeof ResizeObserver === "undefined") return;
    let cancelled = false;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === 0) return;
      if (chartRef.current) {
        chartRef.current.resize();
        return;
      }
      void import("../charts/engine").then(({ createChart }) => {
        if (cancelled || chartRef.current) return;
        const chart = createChart(el);
        chartRef.current = chart;
        if (optionRef.current) chart.setOption(optionRef.current);
        // Zooming asks the page for the zoomed range at a finer resolution, once the gesture settles.
        let settle: ReturnType<typeof setTimeout> | undefined;
        chart.on("dataZoom", () => {
          clearTimeout(settle);
          settle = setTimeout(() => {
            const [z] = (chart.getOption() as { dataZoom?: { start?: number; end?: number }[] }).dataZoom ?? [];
            const ts = timesRef.current;
            const first = ts[0];
            const last = ts.at(-1);
            if (!z || first === undefined || last === undefined) return;
            // At least MIN_ZOOM_MS around the zoom's middle, so the request never asks for an empty or reversed range.
            const lo = first + ((last - first) * (z.start ?? 0)) / 100;
            const hi = first + ((last - first) * (z.end ?? 100)) / 100;
            const half = Math.max(hi - lo, MIN_ZOOM_MS) / 2;
            const mid = (lo + hi) / 2;
            onRangeRef.current?.({
              from: new Date(mid - half).toISOString(),
              to: new Date(mid + half).toISOString(),
            });
          }, 400);
        });
      });
    });
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
      chartRef.current?.dispose();
      chartRef.current = undefined;
    };
  }, [drawable]);

  useEffect(() => {
    if (chartRef.current && option) chartRef.current.setOption(option, { notMerge: true });
  }, [option]);

  const wheel = useWheelCapture(frame, zoom && any);

  const label = any
    ? lines
        .flatMap((l, i) => {
          const st = stats[i];
          const f = { unit: l.unit, decimals: l.decimals };
          return st
            ? [
                t("widgets.chart.summary", {
                  name: l.name,
                  min: fmt.value(st.min, f),
                  max: fmt.value(st.max, f),
                  last: fmt.value(st.last, f),
                }),
              ]
            : [];
        })
        .join("; ")
    : undefined;

  return (
    <Frame widget={widget} state={any ? "normal" : "empty"} title={widget.title}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {units.length ? <span className="text-xs text-gray-700">{units.join(" · ")}</span> : <span />}
        {onRange && (
          <div role="group" aria-label={t("widgets.chart.range")} className="flex gap-1">
            {ranges.map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={shown === r}
                disabled={tooLong(r)}
                title={tooLong(r) ? rangeNote : undefined}
                onClick={() => onRange(r)}
                className={`rounded px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:text-gray-400 ${shown === r ? "bg-brand text-white" : "text-gray-800 hover:bg-gray-100"}`}
              >
                {rangeLabel(r, t)}
              </button>
            ))}
          </div>
        )}
      </div>
      {lines.length > 1 && (
        <ul aria-label={t("widgets.chart.series")} className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-800">
          {lines.map((l, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="size-2 rounded-full"
                style={{ background: COLOURS[i % COLOURS.length] }}
              />
              {l.name}
            </li>
          ))}
        </ul>
      )}
      {limited && rangeNote && <p className="text-xs text-gray-700">{rangeNote}</p>}
      {dropped > 0 && <Note tone="warn">{t("editor.twoUnits")}</Note>}
      {any ? (
        <div
          ref={frame}
          data-wheel-zoom={wheel === "chart" || undefined}
          // On a touch screen a vertical swipe still scrolls the page; a sideways drag pans and a pinch zooms the
          // chart (web-on-phones WOP-06, as on the phones, MP-10).
          className={`relative min-h-40 flex-1 rounded${zoom ? " touch-pan-y" : ""}${wheel === "chart" ? " ring-2 ring-green-600" : ""}`}
        >
          <div ref={host} role="img" aria-label={label} className="absolute inset-0" />
          {wheel !== "page" && (
            <span
              aria-hidden="true"
              className={`pointer-events-none absolute top-1 right-1 rounded bg-white/90 px-1.5 py-0.5 text-xs shadow-sm ${wheel === "chart" ? "text-green-800" : "text-gray-700"}`}
            >
              {t(wheel === "chart" ? "widgets.chart.wheelZooms" : "widgets.chart.clickToZoom")}
            </span>
          )}
        </div>
      ) : (
        <Note>{fmt.t("state.noData")}</Note>
      )}
    </Frame>
  );
}

type Part = {
  name: string;
  unit: string | null;
  decimals: number;
  expected: Band | undefined;
  history: Parameters<typeof toChartSeries>[0];
};

/**
 * The drawn lines, kept the same object while every part is: the input changes with each latest value, and a new
 * option would redraw the chart for nothing.
 */
function useLines(parts: Part[]): ChartLine[] {
  const build = () => ({ parts, lines: parts.map(({ history, ...l }) => ({ ...l, series: toChartSeries(history) })) });
  const [cached, setCached] = useState(build);
  const same =
    cached.parts.length === parts.length &&
    parts.every((p, i) => {
      const q = cached.parts[i];
      return (
        q?.name === p.name &&
        q.unit === p.unit &&
        q.decimals === p.decimals &&
        q.expected?.[0] === p.expected?.[0] &&
        q.expected?.[1] === p.expected?.[1] &&
        q.history === p.history
      );
    });
  if (same) return cached.lines;
  // React's pattern for state derived from the previous render: set it while rendering.
  const next = build();
  setCached(next);
  return next.lines;
}

function rangeLabel(r: string, t: (k: string, o?: Record<string, unknown>) => string) {
  const ms = durationMs(r) ?? 0;
  const days = ms / 86_400_000;
  return Number.isInteger(days) && days > 1
    ? t("widgets.chart.days", { count: days })
    : t("widgets.chart.hours", { count: ms / 3_600_000 });
}

export function LineChart(props: WidgetProps) {
  return <HistoryChart {...props} kind="line" />;
}

export function BarChart(props: WidgetProps) {
  return <HistoryChart {...props} kind="bar" />;
}

export type { ChartRange };
